/**
 * Dashboard view „Fristen“ (DEV-137): every vulnerability case on one time
 * axis — where it stands in the Art. 14 process, what is reported and how much
 * time is left. A click opens the case's own timeline. KEV hits whose product
 * versions are still undecided have no deadline yet; they are listed below as
 * initial assessments with their age.
 *
 * Read-only: reports are recorded in the triage (vulnerability case tab).
 */
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import Banner from '../../components/Banner';
import CveLink from '../../components/CveLink';
import DataTable, { type DataTableColumn } from '../../components/DataTable';
import { KpiRow, type KpiItem } from '../../components/KpiTile';
import { PageSpinner } from '../../components/Spinner';
import { errorMessage, fetchDeadlineTimeline } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { DeadlineTimelineResponse, TimelineTriageRow } from '../../lib/types';
import { AUTO_REFRESH_MS } from '../dashboard/dashboardModel';
import { STATUS_LABELS } from '../triage/eventHelpers';

import TimelineRow from './TimelineRow';
import {
  RANGE_OPTIONS,
  axisTicks,
  hoursLabel,
  matchesFilters,
  position,
  timeWindow,
  type TimelineFilters,
  type TimelineFocus,
  type TimelineRange,
} from './timelineModel';

import './meldefristen.css';

const INITIAL_FILTERS: TimelineFilters = { search: '', scope: 'open', product: '', focus: null };
const EMPTY_TRIAGE: readonly TimelineTriageRow[] = [];

/** The undecided KEV hits: no case and no deadline yet. */
function TriageTable({ rows }: Readonly<{ rows: readonly TimelineTriageRow[] }>): ReactNode {
  const { t } = useLingui();
  const columns: readonly DataTableColumn<TimelineTriageRow>[] = [
    { key: 'cve', header: 'CVE', cellClassName: 'tl-nowrap', render: (row) => <CveLink cve={row.cve} /> },
    {
      key: 'component',
      header: t`Komponente`,
      render: (row) => {
        const status = STATUS_LABELS[row.component_status].toLowerCase();
        return (
          <>
            <div>{row.component ?? t`Komponente`}</div>
            <div className="prod">{t`Komponente ${status}`}</div>
          </>
        );
      },
    },
    {
      key: 'products',
      header: t`Offene Produktversionen`,
      render: (row) => (row.products.length > 0 ? row.products.join(', ') : <span className="muted"><Trans>entschieden</Trans></span>),
    },
    {
      key: 'since',
      header: t`KEV-Treffer seit`,
      width: '190px',
      sortValue: (row) => row.since,
      render: (row) => (
        <>
          <div>{fmtDate(row.since)}</div>
          {row.age_hours === null ? null : <div className="prod">{hoursLabel(row.age_hours)}</div>}
        </>
      ),
    },
    {
      key: 'open',
      header: <span className="visually-hidden"><Trans>Aktion</Trans></span>,
      width: '170px',
      align: 'right',
      render: (row) => <Link to={`/triage?event=${row.event_id}`}><Trans>In der Triage öffnen</Trans></Link>,
    },
  ];
  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(row) => row.event_id}
      caption={t`KEV-Treffer in der Erstprüfung`}
      mobileBreakpoint={760}
      emptyText={t`Alle KEV-Treffer sind entschieden.`}
    />
  );
}

export interface DeadlineTimelineProps {
  /** A case to open and scroll to (`?case=` of a deep link). */
  linkedCaseId: number | null;
  /** When the shown data was built and the warning threshold — the dashboard header shows them. */
  onStand: (stand: string | null) => void;
  /** A focus to start with, e.g. from the dashboard tile „Fällige Meldefristen“ (DEV-133). */
  initialFocus?: TimelineFocus;
}

export default function DeadlineTimeline({ linkedCaseId, onStand, initialFocus = null }: Readonly<DeadlineTimelineProps>): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const [data, setData] = useState<DeadlineTimelineResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [range, setRange] = useState<TimelineRange>('14');
  const [filters, setFilters] = useState<TimelineFilters>({ ...INITIAL_FILTERS, focus: initialFocus });
  const [openId, setOpenId] = useState<number | null>(linkedCaseId);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await fetchDeadlineTimeline());
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err));
    }
  }, []);

  // Refreshed like the overview: on mount and every minute while the tab is visible.
  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (!document.hidden) void load();
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  // `locale`: the stand is translated, hand it up again on a language switch.
  useEffect(() => {
    if (data === null) {
      onStand(null);
      return;
    }
    const generatedAt = fmtDate(data.generated_at);
    const warnHours = data.warn_hours;
    onStand(t`Stand ${generatedAt} · Warnung ${warnHours} h vor Ablauf`);
  }, [data, onStand, t, locale]);

  // A deep link opens its case and scrolls to it once the rows are there.
  useEffect(() => {
    if (data !== null && linkedCaseId !== null) {
      setFilters((current) => ({ ...current, scope: 'all' }));
      setOpenId(linkedCaseId);
      document.getElementById(`meldefristen-row-${linkedCaseId}`)?.scrollIntoView({ block: 'center' });
    }
  }, [data, linkedCaseId]);

  const now = useMemo(() => (data ? Date.parse(data.generated_at) : Date.now()), [data]);
  const rows = useMemo(() => (data?.cases ?? []).filter((row) => matchesFilters(row, filters)), [data, filters]);
  const axis = useMemo(() => timeWindow(range, rows, now), [range, rows, now]);
  // `locale`: the tick labels are dates in the UI language.
  const ticks = useMemo(() => axisTicks(axis), [axis, locale]);
  const today = position(new Date(now).toISOString(), axis);
  const products = useMemo(
    () => [...new Set((data?.cases ?? []).map((row) => row.product_name).filter((name): name is string => !!name))].sort(),
    [data],
  );

  const kpis = useMemo<KpiItem[]>(() => {
    const summary = data?.summary;
    if (!summary) return [];
    const warnHours = data.warn_hours;
    const triageOpen = summary.triage_open;
    const focusTile = (focus: Exclude<TimelineFocus, null>, item: Omit<KpiItem, 'selected' | 'onClick'>): KpiItem => {
      const { label, value } = item;
      return {
        ...item,
        key: focus,
        selected: filters.focus === focus,
        controls: 'meldefristen-cases',
        ariaLabel: t`${label}: ${value} anzeigen`,
        onClick: () => setFilters((current) => ({ ...current, focus: current.focus === focus ? null : focus })),
      };
    };
    return [
      focusTile('overdue', {
        label: t`Überschritten`, value: summary.overdue, tone: summary.overdue > 0 ? 'critical' : 'good',
        title: t`Fälle mit einer Frist der Behördenmeldung, die abgelaufen ist, ohne dass gemeldet wurde`,
      }),
      focusTile('due_24h', {
        label: t`In 24 h fällig`, value: summary.due_24h, tone: summary.due_24h > 0 ? 'warn' : 'good',
        title: t`Fälle mit einer Frist in den nächsten 24 Stunden; gelb ab ${warnHours} h vor Ablauf`,
      }),
      focusTile('waiting', {
        label: t`Wartet auf Abhilfemaßnahme`, value: summary.waiting_corrective, tone: 'neutral',
        title: t`Die 14-Tage-Frist für den Abschlussbericht beginnt erst mit der erfassten Abhilfemaßnahme`,
      }),
      {
        key: 'triage',
        label: t`Erstprüfung offen`, value: triageOpen, tone: triageOpen > 0 ? 'warn' : 'good',
        title: t`KEV-Treffer, deren Produktversionen noch nicht entschieden sind: Die Frist läuft noch nicht`,
        ariaLabel: t`Erstprüfung offen: ${triageOpen} anzeigen`,
        controls: 'meldefristen-triage',
        onClick: () => document.getElementById('meldefristen-triage')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      },
    ];
    // `locale`: the tiles are translated, re-build them on a language switch.
  }, [data, filters.focus, t, locale]);

  if (data === null && loadError === null) return <PageSpinner text={t`Meldefristen werden geladen…`} />;

  const caseCount = rows.length;
  const triageCount = data?.triage.length ?? 0;
  const filtered = filters.search.trim() !== '' || filters.product !== '' || filters.focus !== null || filters.scope !== 'open';
  return (
    <>
      {loadError ? <Banner kind="error"><Trans>Meldefristen konnten nicht geladen werden: {loadError}</Trans></Banner> : null}
      {kpis.length > 0 ? <KpiRow counters={kpis} label={t`Stand der Meldefristen`} /> : null}

      <section id="meldefristen-cases" className="card">
        <div className="card-head table-head">
          <h2><Trans>Meldefristen der Schwachstellenfälle</Trans></h2>
        </div>
        <div className="table-toolbar">
          <input type="search" value={filters.search} placeholder={t`CVE oder Produkt suchen…`} aria-label={t`Fälle durchsuchen`}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
          <select value={filters.scope} aria-label={t`Fälle`}
            onChange={(event) => setFilters((current) => ({ ...current, scope: event.target.value as TimelineFilters['scope'] }))}>
            <option value="open">{t`Offene Fälle`}</option>
            <option value="all">{t`Alle Fälle`}</option>
          </select>
          <select value={filters.product} aria-label={t`Produkt`}
            onChange={(event) => setFilters((current) => ({ ...current, product: event.target.value }))}>
            <option value="">{t`Alle Produkte`}</option>
            {products.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <select value={range} aria-label={t`Zeitraum`} onChange={(event) => setRange(event.target.value as TimelineRange)}>
            {RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{i18n._(option.label)}</option>)}
          </select>
          <div className="table-toolbar-options">
            {filtered ? (
              <button type="button" className="link" onClick={() => setFilters(INITIAL_FILTERS)}><Trans>Filter zurücksetzen</Trans></button>
            ) : null}
            <span className="table-count" aria-live="polite"><Plural value={caseCount} one="# Fall" other="# Fälle" /></span>
          </div>
        </div>

        <div className="tl-axis" aria-hidden="true">
          <span />
          <span className="tl-axis-track">
            {ticks.map((tick) => <span key={tick.at} className="tl-tick" style={{ left: `${tick.left}%` }}>{tick.label}</span>)}
            {today !== null ? <span className="tl-today tl-today-label" style={{ left: `${today}%` }}><Trans>heute</Trans></span> : null}
          </span>
        </div>

        {rows.length === 0 ? (
          <p className="muted tl-empty">
            {(data?.cases.length ?? 0) === 0
              ? <Trans>Noch kein Schwachstellenfall. Er entsteht mit der ersten bestätigten Produktbewertung „Betroffen“.</Trans>
              : <Trans>Kein Fall passt zu den Filtern.</Trans>}
          </p>
        ) : (
          <ul className="tl-rows">
            {rows.map((row) => (
              <TimelineRow key={row.id} row={row} axis={axis} today={today} open={openId === row.id}
                onToggle={() => setOpenId((current) => (current === row.id ? null : row.id))} />
            ))}
          </ul>
        )}
        <p className="hint tl-legend">
          <span className="tl-key tl-key-triage" /> <Trans>Erstprüfung</Trans>
          <span className="tl-key tl-key-report" /> <Trans>Meldephase</Trans>
          <span className="tl-key tl-key-final" /> <Trans>Abschlussbericht</Trans>
          <span className="tl-key tl-key-due" /> <Trans>Frist</Trans>
          <span className="tl-key tl-key-mark" /> <Trans>Meldung, Kundenmail, Advisory</Trans>
        </p>
      </section>

      <section id="meldefristen-triage" className="card tl-triage-card">
        <div className="card-head table-head">
          <h2><Trans>Erstprüfung · Frist läuft noch nicht</Trans></h2>
          <span className="table-count">
            <Plural value={triageCount} one="# KEV-Treffer" other="# KEV-Treffer" />
          </span>
        </div>
        <TriageTable rows={data?.triage ?? EMPTY_TRIAGE} />
      </section>
    </>
  );
}
